import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import PropertyLeadForm from './forms/PropertyLeadForm';
import PartnerLeadForm from './forms/PartnerLeadForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Landing page pubblica per i video del Guest Engagement.
 * URL: /v/{token}
 *
 * - Brand HW (logo, colori)
 * - Mobile-first
 * - Video player nativo (HTML5)
 * - CTA contestuale:
 *    - V1/V2 → link esterno
 *    - V3 → form "Affidaci immobile" (modale)
 *    - V4 → form "Diventa Partner" (modale)
 * - Tracking eventi (play, completed, cta_clicked, lead_submitted)
 */
export default function GuestVideoLanding() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [hasCompleted, setHasCompleted] = useState(false);
  const [showPropertyForm, setShowPropertyForm] = useState(false);
  const [showPartnerForm, setShowPartnerForm] = useState(false);
  const [submittedType, setSubmittedType] = useState(null); // 'property' | 'partner' | null
  const [showVideo, setShowVideo] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/guest-video/${token}`);
        setData(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Link non valido o scaduto');
      }
    })();
  }, [token]);

  const logEvent = async (event_type, extra = {}) => {
    try {
      await axios.post(`${API}/public/guest-video/${token}/event`, { event: event_type, ...extra });
    } catch (e) { /* silent */ }
  };

  const onPlay = () => {
    if (!hasPlayed) {
      setHasPlayed(true);
      logEvent('video_played');
    }
  };
  const onEnded = () => {
    if (!hasCompleted) {
      setHasCompleted(true);
      logEvent('video_completed', { progress_pct: 100 });
    }
  };
  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    const pct = (v.currentTime / v.duration) * 100;
    if (pct >= 75 && !v._logged75) { v._logged75 = true; logEvent('video_progress', { progress_pct: 75 }); }
    else if (pct >= 50 && !v._logged50) { v._logged50 = true; logEvent('video_progress', { progress_pct: 50 }); }
    else if (pct >= 25 && !v._logged25) { v._logged25 = true; logEvent('video_progress', { progress_pct: 25 }); }
  };

  const handleCtaClick = (style, target) => {
    logEvent('cta_clicked', { cta_target: target });
    if (style === 'form_property') {
      setShowPropertyForm(true);
    } else if (style === 'form_partner') {
      setShowPartnerForm(true);
    } else {
      window.open(target, '_blank', 'noopener');
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 via-amber-50 to-sky-50 px-4">
        <div className="max-w-md text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Link non disponibile</h1>
          <p className="text-slate-600">{error}</p>
          <a href="https://www.holidayworld.it" className="inline-block mt-6 text-rose-600 hover:underline">Vai al sito holidayworld.it</a>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 via-amber-50 to-sky-50">
        <div className="w-12 h-12 border-4 border-rose-300 border-t-rose-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  // Localized strings (minimal i18n inline)
  const L = (data.language || 'it').toLowerCase();
  const t = (key) => ({
    greeting: { it: 'Un messaggio per te', en: 'A message for you', fr: 'Un message pour toi', de: 'Eine Nachricht für dich', es: 'Un mensaje para ti', nl: 'Een bericht voor jou', pl: 'Wiadomość dla Ciebie', pt: 'Uma mensagem para ti' },
    duration: { it: 'Durata circa 30 secondi', en: 'About 30 seconds', fr: 'Environ 30 secondes', de: 'Etwa 30 Sekunden', es: 'Aprox. 30 segundos', nl: 'Ongeveer 30 seconden', pl: 'Około 30 sekund', pt: 'Cerca de 30 segundos' },
    thanks: { it: 'Grazie per aver scelto Holiday World', en: 'Thanks for choosing Holiday World', fr: 'Merci d\'avoir choisi Holiday World', de: 'Danke, dass du Holiday World gewählt hast', es: 'Gracias por elegir Holiday World', nl: 'Bedankt dat je Holiday World hebt gekozen', pl: 'Dziękujemy za wybór Holiday World', pt: 'Obrigado por escolher a Holiday World' },
    by: { it: 'da', en: 'from', fr: 'de', de: 'von', es: 'de', nl: 'van', pl: 'od', pt: 'de' },
    submitted_property: { it: 'Richiesta inviata! Ti contatteremo entro 48 ore.', en: 'Request sent! We will contact you within 48 hours.', fr: 'Demande envoyée ! Nous vous contacterons sous 48 heures.', de: 'Anfrage gesendet! Wir melden uns innerhalb von 48 Stunden.', es: '¡Solicitud enviada! Te contactaremos en 48 horas.', nl: 'Aanvraag verstuurd! We nemen binnen 48 uur contact op.', pl: 'Zgłoszenie wysłane! Skontaktujemy się w ciągu 48 godzin.', pt: 'Pedido enviado! Entraremos em contacto em 48 horas.' },
    submitted_partner: { it: 'Candidatura ricevuta! Riceverai a breve un messaggio WhatsApp con i prossimi passi.', en: 'Application received! You\'ll get a WhatsApp message with the next steps shortly.', fr: 'Candidature reçue ! Vous recevrez bientôt un message WhatsApp.', de: 'Bewerbung erhalten! Sie erhalten in Kürze eine WhatsApp-Nachricht.', es: '¡Candidatura recibida! Recibirás pronto un mensaje de WhatsApp.', nl: 'Aanmelding ontvangen! Je krijgt binnenkort een WhatsApp-bericht met de volgende stappen.', pl: 'Zgłoszenie odebrane! Wkrótce otrzymasz wiadomość WhatsApp z kolejnymi krokami.', pt: 'Candidatura recebida! Receberás em breve uma mensagem de WhatsApp com os próximos passos.' },
    // Trasparenza AI (Art. 50 AI Act, obbligo dal 2 agosto 2026)
    ai_disclosure: { it: 'Video generato con intelligenza artificiale', en: 'Video generated with artificial intelligence', fr: 'Vidéo générée par intelligence artificielle', de: 'Mit künstlicher Intelligenz erstelltes Video', es: 'Vídeo generado con inteligencia artificial', nl: 'Video gegenereerd met kunstmatige intelligentie', pl: 'Wideo wygenerowane przez sztuczną inteligencję', pt: 'Vídeo gerado com inteligência artificial' },
    rewatch: { it: 'Rivedi il video', en: 'Watch the video again', fr: 'Revoir la vidéo', de: 'Video erneut ansehen', es: 'Volver a ver el vídeo', nl: 'Bekijk de video opnieuw', pl: 'Obejrzyj wideo ponownie', pt: 'Rever o vídeo' },
    form_intro_property: { it: 'Vuoi far rendere il tuo immobile? Lascia i tuoi contatti: ti richiamiamo noi entro 48 ore.', en: 'Want to make your property earn? Leave your details and we\'ll call you back within 48 hours.', fr: 'Vous voulez rentabiliser votre bien ? Laissez vos coordonnées, nous vous rappelons sous 48 heures.', de: 'Möchtest du mit deiner Immobilie verdienen? Hinterlasse deine Daten, wir melden uns innerhalb von 48 Stunden.', es: '¿Quieres rentabilizar tu inmueble? Déjanos tus datos y te llamamos en 48 horas.', nl: 'Wil je je woning laten renderen? Laat je gegevens achter, we bellen je binnen 48 uur terug.', pl: 'Chcesz zarabiać na swojej nieruchomości? Zostaw dane kontaktowe — oddzwonimy w ciągu 48 godzin.', pt: 'Queres rentabilizar o teu imóvel? Deixa os teus contactos e ligamos-te em 48 horas.' },
    form_intro_partner: { it: 'Vuoi collaborare con Holiday World nella tua zona? Candidati: ti contattiamo con i prossimi passi.', en: 'Want to work with Holiday World in your area? Apply and we\'ll contact you with the next steps.', fr: 'Envie de collaborer avec Holiday World dans votre région ? Postulez, nous vous contactons avec les prochaines étapes.', de: 'Möchtest du mit Holiday World in deiner Region zusammenarbeiten? Bewirb dich, wir melden uns mit den nächsten Schritten.', es: '¿Quieres colaborar con Holiday World en tu zona? Postúlate y te contactamos con los próximos pasos.', nl: 'Wil je samenwerken met Holiday World in jouw regio? Meld je aan, we nemen contact op met de volgende stappen.', pl: 'Chcesz współpracować z Holiday World w swoim regionie? Zgłoś się — skontaktujemy się z kolejnymi krokami.', pt: 'Queres colaborar com a Holiday World na tua zona? Candidata-te e entramos em contacto com os próximos passos.' },
  }[key]?.[L] || ({ greeting: 'A message for you', duration: 'About 30 seconds', thanks: 'Thanks for choosing Holiday World', by: 'from', submitted_property: 'Request sent! We will contact you soon.', submitted_partner: 'Application received! You\'ll hear from us shortly.', ai_disclosure: 'Video generated with artificial intelligence', rewatch: 'Watch the video again', form_intro_property: 'Leave your details and we\'ll call you back within 48 hours.', form_intro_partner: 'Apply and we\'ll contact you with the next steps.' })[key]);

  const ctaLabel = data.cta?.label || 'Holiday World';
  const ctaTarget = data.cta?.target || 'https://www.holidayworld.it';
  const ctaStyle = data.cta?.style || 'secondary';
  const ctaPrimary = ctaStyle === 'primary' || ctaStyle === 'form_property' || ctaStyle === 'form_partner';
  // V3/V4: il video arriva già dentro WhatsApp → sulla landing il form è protagonista, il video secondario
  const isFormCampaign = ctaStyle === 'form_property' || ctaStyle === 'form_partner';

  const videoBlock = (
    <div className="rounded-2xl overflow-hidden bg-black shadow-xl shadow-rose-200/50 relative">
      <video
        ref={videoRef}
        src={data.video_url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${data.video_url}` : data.video_url}
        controls
        playsInline
        preload="metadata"
        onPlay={onPlay}
        onEnded={onEnded}
        onTimeUpdate={onTimeUpdate}
        className="w-full aspect-[9/16] object-contain bg-black"
        data-testid="landing-video-player"
      />
      {/* Etichetta trasparenza AI sempre visibile sul player (Art. 50 AI Act) */}
      <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-sm text-white/90 text-[10px] leading-tight px-2 py-1 rounded-full pointer-events-none select-none" data-testid="landing-ai-disclosure-overlay">
        {t('ai_disclosure')}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-rose-50/40">
      {data.is_preview && (
        <div className="bg-amber-500 text-white text-center text-xs font-medium py-1.5" data-testid="landing-preview-banner">
          ⚠️ ANTEPRIMA — Questo è un link di test, non viene loggato
        </div>
      )}
      
      {/* Header brand */}
      <header className="px-4 pt-6 pb-3 text-center">
        <img
          src="/hw-logo-blu.png"
          alt="Holiday World"
          className="h-14 sm:h-16 mx-auto w-auto"
          data-testid="landing-brand-logo"
        />
      </header>

      <main className="max-w-md mx-auto px-4 pb-12">
        {/* Saluto personalizzato */}
        <div className="text-center mt-4 mb-4">
          {data.guest_first_name && (
            <h2 className="text-2xl font-bold text-slate-800" data-testid="landing-greeting">Ciao {data.guest_first_name}! 👋</h2>
          )}
          <p className="text-slate-600 mt-1 text-sm">
            {t('greeting')} {t('by')} <strong>Holiday World</strong>
          </p>
          {data.property_name && (
            <p className="text-xs text-slate-400 mt-1">{data.property_name}</p>
          )}
        </div>

        {/* Layout V1/V2: video protagonista */}
        {!isFormCampaign && (
          <>
            <div className="mb-4">{videoBlock}</div>
            <p className="text-xs text-slate-400 text-center mb-1">{t('duration')}</p>
            <p className="text-[11px] text-slate-500 text-center mb-6" data-testid="landing-ai-disclosure-badge">
              {t('ai_disclosure')}
            </p>

            {submittedType ? (
              <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-5 text-center" data-testid="landing-submit-success">
                <div className="text-4xl mb-2">✅</div>
                <p className="text-emerald-800 font-medium">
                  {submittedType === 'property' ? t('submitted_property') : t('submitted_partner')}
                </p>
              </div>
            ) : (
              <button
                onClick={() => handleCtaClick(ctaStyle, ctaTarget)}
                className={`w-full py-4 px-6 rounded-2xl font-semibold text-base shadow-lg transition-all transform active:scale-95 ${
                  ctaPrimary
                    ? 'bg-gradient-to-r from-rose-600 to-pink-600 text-white shadow-rose-300/50 hover:shadow-rose-400/60'
                    : 'bg-white border-2 border-rose-200 text-rose-700 hover:bg-rose-50'
                }`}
                data-testid="landing-cta-btn"
              >
                {ctaLabel} →
              </button>
            )}
          </>
        )}

        {/* Layout V3/V4: form in primo piano, video ridotto sotto */}
        {isFormCampaign && (
          <>
            {submittedType ? (
              <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-5 text-center" data-testid="landing-submit-success">
                <div className="text-4xl mb-2">✅</div>
                <p className="text-emerald-800 font-medium">
                  {submittedType === 'property' ? t('submitted_property') : t('submitted_partner')}
                </p>
              </div>
            ) : ctaStyle === 'form_property' ? (
              <div data-testid="landing-form-hero">
                <PropertyLeadForm
                  token={token}
                  language={L}
                  inline
                  onSuccess={() => { setSubmittedType('property'); logEvent('lead_submitted_property'); }}
                />
              </div>
            ) : (
              <div data-testid="landing-form-hero">
                <PartnerLeadForm
                  token={token}
                  language={L}
                  inline
                  onSuccess={() => { setSubmittedType('partner'); logEvent('lead_submitted_partner'); }}
                />
              </div>
            )}

            <div className="mt-6">
              {!showVideo ? (
                <button
                  onClick={() => setShowVideo(true)}
                  className="w-full py-3 px-4 rounded-2xl text-sm font-medium bg-white border-2 border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
                  data-testid="landing-rewatch-btn"
                >
                  ▶ {t('rewatch')}
                </button>
              ) : (
                <>
                  {videoBlock}
                  <p className="text-[11px] text-slate-500 text-center mt-2" data-testid="landing-ai-disclosure-badge">
                    {t('ai_disclosure')}
                  </p>
                </>
              )}
            </div>
          </>
        )}

        {/* Trust signal */}
        <p className="text-center text-xs text-slate-400 mt-8">
          {t('thanks')}
        </p>
      </main>

      <footer className="text-center py-4 text-xs text-slate-300 border-t border-slate-100">
        © Holiday World — Property Management
      </footer>
      
      {/* Modal Form V3 - Proprietario */}
      {showPropertyForm && (
        <PropertyLeadForm
          token={token}
          language={L}
          onClose={() => setShowPropertyForm(false)}
          onSuccess={() => { setShowPropertyForm(false); setSubmittedType('property'); logEvent('lead_submitted_property'); }}
        />
      )}

      {/* Modal Form V4 - Partner */}
      {showPartnerForm && (
        <PartnerLeadForm
          token={token}
          language={L}
          onClose={() => setShowPartnerForm(false)}
          onSuccess={() => { setShowPartnerForm(false); setSubmittedType('partner'); logEvent('lead_submitted_partner'); }}
        />
      )}
      <a href="/privacy" data-testid="video-privacy-link"
        className="block pb-6 text-center text-xs text-slate-400 hover:text-slate-600 underline">
        Privacy Policy
      </a>
    </div>
  );
}
